import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { SaveSession } from "@/components/SaveSession";
import { FurtherReading } from "@/components/FurtherReading";
import { CheckCircle2, Award, RotateCcw, XCircle } from "lucide-react";

/* ── phase stepper (defaults to the full anaesthetic-machine workflow,
   but any sim can pass its own PHASES + ORDER arrays) ──────────────── */
const DEFAULT_PHASES = [
  { key: "setup", label: "Initial setup" },
  { key: "leak", label: "Leak test" },
  { key: "ippv", label: "IPPV" },
];
const DEFAULT_ORDER = ["setup", "leak", "ippv", "done"];

export const PhaseStepper = ({ phase, phases = DEFAULT_PHASES, order = DEFAULT_ORDER }) => {
  const cur = order.indexOf(phase);
  return (
    <div className="flex items-center gap-1.5" data-testid="phase-stepper">
      {phases.map((p, i) => {
        const done = cur > i;
        const active = order[cur] === p.key;
        return (
          <div key={p.key} className="flex items-center gap-1.5 flex-1">
            <div
              data-testid={`phase-pill-${p.key}`}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-[11px] font-semibold w-full justify-center transition-colors ${
                active ? "bg-[#0047AB] text-white border-[#0047AB]"
                : done ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-white text-slate-400 border-slate-200"
              }`}
            >
              {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : <span className="w-3.5 text-center">{i + 1}</span>}
              <span className="truncate">{p.label}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* ── debrief ──────────────────────────────────────────────────────── */
export const MachineDebrief = ({
  result,
  onReset,
  simulatorId = "ippv",
  simulatorName = "Anaesthetic Machine",
  gradeContext = "Cert IV anaesthetic machine",
}) => {
  const band = result.total >= 80 ? { t: "text-emerald-600", r: "border-emerald-300 bg-emerald-50", label: "Competent" }
    : result.total >= 60 ? { t: "text-amber-600", r: "border-amber-300 bg-amber-50", label: "Developing" }
    : { t: "text-rose-600", r: "border-rose-300 bg-rose-50", label: "Not yet competent" };
  const summary =
    `Grade ${result.grade} · ` +
    result.phases.map((p) => `${p.label} ${p.score}/${p.max}`).join(" · ");
  return (
    <div className="space-y-5 fade-up">
      <Card className="border-emerald-200 shadow-lg" data-testid="machine-debrief">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="flex items-center gap-4">
              <div className={`flex flex-col items-center justify-center w-24 h-24 rounded-2xl border-4 ${band.r}`} data-testid="machine-grade">
                <Award className={`w-5 h-5 ${band.t}`} />
                <span className={`text-4xl font-bold leading-none ${band.t}`} data-testid="machine-score">{result.total}</span>
                <span className="text-[10px] text-slate-400 font-semibold">/100</span>
              </div>
              <div>
                <div className={`text-xl font-bold ${band.t}`} data-testid="machine-band">{band.label}</div>
                <div className="text-xs text-slate-500 mt-1">Grade {result.grade} · {gradeContext}</div>
              </div>
            </div>
            <div className="lg:col-span-2" data-testid="machine-breakdown">
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Phase scores</div>
              <div className="space-y-2">
                {result.phases.map((p) => {
                  const pct = Math.round((p.score / p.max) * 100);
                  return (
                    <div key={p.key} data-testid={`machine-phase-${p.key}`}>
                      <div className="flex items-center justify-between text-xs mb-0.5">
                        <span className="text-slate-700 font-medium">{p.label}</span>
                        <span className="font-mono-clinical font-semibold text-slate-800">{p.score}/{p.max}</span>
                      </div>
                      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: pct >= 80 ? "#10b981" : pct >= 50 ? "#f59e0b" : "#ef4444" }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          {result.mistakes.length > 0 && (
            <ul className="mt-4 space-y-1 text-[11px] text-slate-500 border-t border-slate-100 pt-3" data-testid="machine-mistakes">
              {result.mistakes.map((m, i) => (
                <li key={i} className="flex items-start gap-1.5">
                  <XCircle className="w-3 h-3 text-red-400 mt-0.5 shrink-0" />
                  <span><span className="uppercase text-[9px] font-bold text-slate-400 mr-1">{m.phase || m.round}</span>{m.msg}</span>
                </li>
              ))}
            </ul>
          )}
          <Button onClick={onReset} data-testid="machine-reset-btn" className="mt-5 bg-[#0047AB] hover:bg-[#003380] text-white">
            <RotateCcw className="w-4 h-4 mr-1.5" /> Run it again
          </Button>
        </CardContent>
      </Card>
      <SaveSession simulatorId={simulatorId} simulatorName={simulatorName} score={result.total} grade={band.label} summary={summary} />
      <FurtherReading topic="ventilation" />
    </div>
  );
};
